import type { Ref } from 'vue'
import type { Meta } from '@brighterly/lib-core-types/api/interfaces'
import type { TutorSlotLog } from '../tutor-slot-log/interfaces'
import type { TutorSlotLogActor } from '../tutor-slot-log/interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'
import { DEFAULT_APP_TABLE_META } from '@shared/ui/tables'

import { TutorSlotLogAction } from '../tutor-slot-log/enums'
import { TutorSlotLogField } from '../tutor-slot-log/enums'
import { useTutorSlotLogStore } from '../tutor-slot-log/store'
import { useTutorSlotLogsApi } from '../../api/tutor-slots/logs.hook'

interface TableTutorSlotLogReturn {
  logs: ComputedRef<TutorSlotLog[]>
  isLoading: ComputedRef<boolean>
  hasLogs: ComputedRef<boolean>
  meta: ComputedRef<Meta>
  refExpandedId: Ref<number | null>
  formatDateTime: (value: string | null) => string
  formatAction: (log: TutorSlotLog) => string
  formatChange: (log: TutorSlotLog) => string
  formatActorLabel: (log: TutorSlotLog) => string
  getBookingId: (log: TutorSlotLog) => string | null
  isExpanded: (id: number) => boolean
  toggleExpand: (id: number) => void
  setChangePage: (value: number) => Promise<void>
  setChangePerPage: (value: number) => Promise<void>
}

const FIELD_LABELS: Record<TutorSlotLogField, string> = {
  [TutorSlotLogField.Status]: 'Slot status',
  [TutorSlotLogField.BookingId]: 'Booking',
}

const ACTION_VERBS: Record<TutorSlotLogAction, string> = {
  [TutorSlotLogAction.Created]: 'created',
  [TutorSlotLogAction.Updated]: 'updated',
}

const formatActorName = (actor: TutorSlotLogActor): string => {
  if (actor.display_name) return actor.display_name
  const parts = [actor.first_name, actor.last_name].filter(Boolean)
  if (parts.length) return parts.join(' ')
  return actor.email ?? `#${actor.id}`
}

export const useTableTutorSlotLog = (): TableTutorSlotLogReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useTutorSlotLogStore()
  const dayjs = useDayjs()

  // Single-row expand: matches the main-app convention
  // (one open row id, toggled to null to collapse).
  const refExpandedId = ref<number | null>(null)

  const logs = computed(() => store.getLogs)
  const isLoading = computed(() => store.isLoading)
  const hasLogs = computed(() => store.hasLogs)
  const meta = computed<Meta>(() => store.getMeta ?? DEFAULT_APP_TABLE_META)

  const isExpanded = (id: number): boolean => refExpandedId.value === id

  const toggleExpand = (id: number): void => {
    refExpandedId.value = refExpandedId.value === id ? null : id
  }

  const formatDateTime = (value: string | null): string =>
    value ? dayjs(value).format(DateFormat.KanbanDate) : ''

  const formatAction = (log: TutorSlotLog): string => {
    const label = FIELD_LABELS[log.field] ?? log.field
    const verb = ACTION_VERBS[log.action] ?? log.action.toLowerCase()
    return `${label} ${verb}`
  }

  const formatChange = (log: TutorSlotLog): string => {
    const from = log.old_value ?? '—'
    const to = log.value ?? '—'
    return `${from} → ${to}`
  }

  const formatActorLabel = (log: TutorSlotLog): string => {
    if (!log.actor) return 'System'
    return formatActorName(log.actor)
  }

  const getBookingId = (log: TutorSlotLog): string | null => {
    if (log.field !== TutorSlotLogField.BookingId) return null
    return log.value
  }

  const setChangePage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        page: String(value),
      },
    })
    await useTutorSlotLogsApi()
  }

  const setChangePerPage = async (value: number): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        per_page: String(value),
      },
    })
    await useTutorSlotLogsApi()
  }

  return {
    logs,
    isLoading,
    hasLogs,
    meta,
    refExpandedId,
    formatDateTime,
    formatAction,
    formatChange,
    formatActorLabel,
    getBookingId,
    isExpanded,
    toggleExpand,
    setChangePage,
    setChangePerPage,
  }
}
